<template>
	<button
		v-if="isCollapsed"
		type="button"
		class="question-entry"
		:aria-label="t('展开常见问题')"
		@click="toggleCollapse"
	>
		<img :src="faqQuestion" class="question-entry__icon" width="20" height="20" alt="" />
		<span>{{ t("常见问题") }}</span>
	</button>
	<div v-else class="pc-bazi-panel" :class="{ 'pc-bazi-panel--no-profile': !showProfile }">
		<div v-if="showProfile" class="pc-bazi-panel__header">
			<div class="pc-bazi-panel__profile">
				<img class="pc-bazi-panel__user-icon" :src="userAdd" width="18" height="18" alt="" />
				<span class="pc-bazi-panel__name">{{ profile?.name || "" }}</span>
			</div>
			<div class="pc-bazi-panel__header-actions">
				<button type="button" class="pc-bazi-panel__switch" @click="goArchivePage">
					{{ profile?.name ? t("切换档案") : t("添加档案") }}
					<img :src="chevronRight" width="10" height="10" alt="" />
				</button>
				<button
					type="button"
					class="pc-bazi-panel__toggle"
					:aria-label="t('收起常见问题')"
					@click="toggleCollapse"
				>
					<img
						:src="faqCollapseBack"
						class="pc-bazi-panel__toggle-icon"
						width="24"
						height="24"
						alt=""
					/>
				</button>
			</div>
		</div>
		<div class="pc-bazi-panel__questions-wrap">
			<PcQuestionScrollRow ref="scrollRowRef" content-class="pc-bazi-panel__questions">
				<button
					v-for="(q, i) in questions"
					:key="i"
					type="button"
					class="pc-bazi-panel__chip"
					@click="onQuestion(q)"
				>
					{{ q }}
				</button>
			</PcQuestionScrollRow>
		</div>
	</div>
</template>

<script setup lang="ts">
import type { MasterSBaziFill } from "@variants/seer/master-s/shared/types/bazi-fill";
import type { UserMess } from "@variants/seer/master-s/shared/types/room-info";
import {
	chevronRight,
	faqCollapseBack,
	faqQuestion,
	userAdd,
} from "@variants/seer/master-s/shared/images";
import PcQuestionScrollRow from "@variants/seer/master-s/pc/components/PcQuestionScrollRow.vue";
import { getQuestionList } from "../../../../../../api/master-s";
import { t } from "@variants/seer/master-s/shared/utils/i18n";
import { showAddBaziFileToast } from "@variants/seer/master-s/shared/utils/show-add-bazi-toast";

const props = withDefaults(
	defineProps<{
		userMess: UserMess | null;
		/** 未登录等场景可隐藏档案行，仅保留常见问题 */
		showProfile?: boolean;
		/** 变化时重新拉问题列表 / 档案（如 roomInfo 就绪 / 刷新后） */
		reloadKey?: number | string;
		collapsed?: boolean;
	}>(),
	{ showProfile: true, reloadKey: 0 }
);

const emit = defineEmits<{
	send: [text: string];
	profile: [profile: MasterSBaziFill];
	"update:collapsed": [value: boolean];
}>();

const questions = ref<string[]>([]);
const profile = ref<MasterSBaziFill | null>(null);
const scrollRowRef = ref<InstanceType<typeof PcQuestionScrollRow> | null>(null);
const isCollapsed = computed({
	get: () => Boolean(props.collapsed),
	set: (value: boolean) => emit("update:collapsed", value),
});

function applyUserMess(userMess: UserMess | null) {
	if (!userMess) {
		profile.value = null;
		return;
	}
	const sex = userMess.sex === 1 ? t("男") : t("女");
	const birthday = String(userMess.bir_timestamp || "").substring(0, 10);
	profile.value = {
		name: userMess.user_name,
		birthday,
		sex,
	};
	emit("profile", profile.value);
}

function applyBaziFile(
	file: {
		id?: number;
		user_name?: string;
		gender?: number;
		bir_timestamp?: string;
	} | null
) {
	if (!file?.user_name) return;
	applyUserMess({
		id: Number(file.id) || 0,
		sex: Number(file.gender) || 0,
		user_name: file.user_name,
		bir_timestamp: String(file.bir_timestamp || ""),
	});
}

async function loadQuestions() {
	try {
		const list = await getQuestionList(1);
		questions.value = Array.isArray(list) ? list.map(item => String(item)) : [];
	} catch (e) {
		console.error("getQuestionList failed", e);
		questions.value = [];
	}
	await nextTick();
	scrollRowRef.value?.updateScrollState?.();
}

async function loadProfile() {
	applyUserMess(props.userMess);
	if (!props.showProfile || !import.meta.client) return;
	if (!localStorage.getItem("token")) return;
	try {
		const store = useBaziFileStore();
		await store.getbaziFileList();
		applyBaziFile(store.baziFile);
	} catch (e) {
		console.error("getbaziFileList failed", e);
	}
}

async function reloadPanelData() {
	await Promise.all([loadProfile(), loadQuestions()]);
}

watch(
	() => props.reloadKey,
	() => {
		void reloadPanelData();
	},
	{ immediate: true }
);

watch(
	() => props.userMess,
	val => applyUserMess(val),
	{ deep: true }
);

function goArchivePage() {
	navigateTo({
		path: "/seer/user",
		query: { type: "default", from: "master-s" },
	});
}

function onQuestion(q: string) {
	if (props.showProfile && !profile.value?.name) {
		showAddBaziFileToast();
		return;
	}
	emit("send", q);
}

function toggleCollapse() {
	isCollapsed.value = !isCollapsed.value;
	if (!isCollapsed.value) {
		nextTick(() => scrollRowRef.value?.updateScrollState?.());
	}
}

watch(isCollapsed, collapsed => {
	if (!collapsed) {
		nextTick(() => scrollRowRef.value?.updateScrollState?.());
	}
});
</script>

<style scoped>
/* Figma PC：展开 1299:32598 / 收起按钮 1686:41626 / 收起态 1677:31073 */
.question-entry {
	display: inline-flex;
	flex-shrink: 0;
	align-items: center;
	justify-content: center;
	gap: 0;
	height: 42px;
	padding: 8px 16px;
	border: 1px solid #efefef;
	border-radius: 61px;
	background: rgba(255, 255, 255, 0.7);
	color: #333;
	font-size: 16px;
	font-weight: 400;
	line-height: 16px;
	white-space: nowrap;
	cursor: pointer;
	box-sizing: border-box;
}

.question-entry__icon {
	display: block;
	width: 20px;
	height: 20px;
	object-fit: contain;
}

.pc-bazi-panel {
	width: 100%;
	box-sizing: border-box;
	display: flex;
	flex-direction: column;
	gap: 6px;
	padding: 8px 16px;
	border: 1px solid #f7f7f7;
	border-radius: 10px;
	background: linear-gradient(180deg, #fff8e9 0%, #fff 100%);
	overflow: hidden;
}

.pc-bazi-panel--no-profile {
	justify-content: center;
}

.pc-bazi-panel__header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	min-width: 0;
}

.pc-bazi-panel__profile {
	display: flex;
	align-items: center;
	gap: 4px;
	min-width: 0;
}

.pc-bazi-panel__user-icon {
	flex-shrink: 0;
	width: 18px;
	height: 18px;
	object-fit: contain;
}

.pc-bazi-panel__name {
	font-size: 18px;
	font-weight: 400;
	color: #333;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.pc-bazi-panel__header-actions {
	display: flex;
	align-items: center;
	gap: 24px;
	flex-shrink: 0;
}

.pc-bazi-panel__switch {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 0;
	height: 28px;
	padding: 0 6px 0 10px;
	border: none;
	border-radius: 26px;
	background: #f2af19;
	color: #fff;
	font-size: 14px;
	font-weight: 700;
	line-height: 12px;
	white-space: nowrap;
	cursor: pointer;
	flex-shrink: 0;
}

.pc-bazi-panel__switch img {
	display: block;
}

.pc-bazi-panel__toggle {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 24px;
	height: 24px;
	padding: 0;
	border: 0;
	background: transparent;
	cursor: pointer;
}

.pc-bazi-panel__toggle-icon {
	display: block;
	width: 24px;
	height: 24px;
	object-fit: contain;
	transform: rotate(90deg);
}

.pc-bazi-panel__questions-wrap {
	width: 100%;
	min-width: 0;
}

.pc-bazi-panel__questions {
	display: flex;
	flex-wrap: nowrap;
	gap: 8px;
	align-items: center;
}

.pc-bazi-panel__chip {
	display: inline-flex;
	flex-shrink: 0;
	align-items: center;
	justify-content: center;
	padding: 8px;
	border: 0.815px solid #fff4dd;
	border-radius: 61px;
	background: rgba(255, 255, 255, 0.7);
	color: #333;
	font-size: 16px;
	font-weight: 400;
	line-height: 16px;
	white-space: nowrap;
	cursor: pointer;
	box-sizing: border-box;
}
</style>
